import { createSlice } from '@reduxjs/toolkit';

import type {
  DashboardTab,
  MarketAppWithTabs,
  TileBigNumber,
  WidgetV2
} from '@/features/Dashboard/types';

import { cloneApp, cloneTab, cloneTile } from './utils/clone';
import {
  ensureDashboardDraft,
  ensureTabDraft,
  ensureUniqueAppId,
  generateUniqueTabName,
  getCurrentTabDraft,
  isCurrentDashboard,
  markDashboardDirty
} from './utils/helpers';
import { validateDashboardName } from './utils/invariants';
import { mapServerDataToDraft } from './utils/mapping';
import { insertAtIndex, reorderArray } from './utils/reorder';

import type { DashboardEditState, DraftTab, TabDraft } from './dashboardEdit.types';
import type { PayloadAction } from '@reduxjs/toolkit';

const initialState: DashboardEditState = {
  isInitialized: false,
  isDirty: false,
  dashboardName: '',
  initialDashboardName: '',
  draftTabs: [],
  currentTabId: null,
  tabDrafts: {},
  otherDashboardDrafts: {},
  dirtyDashboards: [],
  allDashboardsData: {},
  insertPosition: null
};

// ============================================================================
// Slice with Intent-Based Actions
// ============================================================================

const dashboardEditSlice = createSlice({
  name: 'dashboardEdit',
  initialState,
  reducers: {
    // ========================================================================
    // Session Management
    // ========================================================================

    initializeDraft: (
      state,
      action: PayloadAction<{
        dashboardName: string;
        initialDashboardName: string;
        draftTabs: DraftTab[];
        tabDrafts: Record<string, TabDraft>;
      }>
    ) => {
      const { dashboardName, initialDashboardName, draftTabs, tabDrafts } = action.payload;

      state.isInitialized = true;
      state.isDirty = false;
      state.dashboardName = dashboardName;
      state.initialDashboardName = initialDashboardName;
      state.draftTabs = draftTabs;
      state.tabDrafts = tabDrafts;
      state.currentTabId = draftTabs[0]?.id ?? null;
      state.otherDashboardDrafts = {};
      state.dirtyDashboards = [];
      state.insertPosition = null;
    },

    resetDraft: () => initialState,

    resetToEmpty: state => {
      const mainTabId = crypto.randomUUID();
      state.draftTabs = [{ id: mainTabId, name: 'Main', sourceName: null }];
      state.tabDrafts = { [mainTabId]: { marketTabApps: [], tiles: [] } };
      state.currentTabId = mainTabId;
      state.isDirty = true;
    },

    markDirty: state => {
      state.isDirty = true;
      markDashboardDirty(state, state.dashboardName);
    },

    /**
     * Set all dashboards data from server.
     * Used by thunks to initialize drafts for other dashboards during move operations.
     */
    setAllDashboardsData: (
      state,
      action: PayloadAction<{ data: Record<string, DashboardTab> }>
    ) => {
      state.allDashboardsData = action.payload.data;
    },

    // ========================================================================
    // Dashboard Operations
    // ========================================================================

    renameDashboard: (state, action: PayloadAction<{ name: string }>) => {
      const { name } = action.payload;
      validateDashboardName(name);

      state.dashboardName = name;
      state.isDirty = true;
    },

    // ========================================================================
    // Tab Management
    // ========================================================================

    setCurrentTab: (state, action: PayloadAction<{ tabId: string }>) => {
      const { tabId } = action.payload;
      if (state.draftTabs.some(t => t.id === tabId)) {
        state.currentTabId = tabId;
      }
    },

    addTab: (state, action: PayloadAction<{ tabId: string; name: string }>) => {
      const { tabId, name } = action.payload;

      // Generate a unique name automatically if there's a conflict
      const uniqueName = generateUniqueTabName(state.draftTabs, name);

      state.draftTabs.push({ id: tabId, name: uniqueName, sourceName: null });
      state.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
      state.isDirty = true;
    },

    renameTab: (state, action: PayloadAction<{ tabId: string; name: string }>) => {
      const { tabId, name } = action.payload;

      const tab = state.draftTabs.find(t => t.id === tabId);
      if (tab) {
        tab.name = name;
        state.isDirty = true;
      }
    },

    deleteTab: (state, action: PayloadAction<{ tabId: string }>) => {
      const { tabId } = action.payload;

      if (state.draftTabs.length <= 1) return;

      const tabIndex = state.draftTabs.findIndex(t => t.id === tabId);
      if (tabIndex === -1) return;

      state.draftTabs = state.draftTabs.filter(t => t.id !== tabId);
      delete state.tabDrafts[tabId];

      if (state.currentTabId === tabId) {
        const newIndex = Math.max(0, tabIndex - 1);
        state.currentTabId = state.draftTabs[newIndex]?.id ?? null;
      }

      state.isDirty = true;
    },

    reorderTabs: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      state.draftTabs = reorderArray(state.draftTabs, fromIndex, toIndex);
      state.isDirty = true;
    },

    // ========================================================================
    // App Operations (within current tab)
    // ========================================================================

    addApp: (
      state,
      action: PayloadAction<{
        tabId: string;
        app: MarketAppWithTabs;
        insertAtIndex?: number;
      }>
    ) => {
      const { tabId, app, insertAtIndex: insertIdx } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const clonedApp = cloneApp(app);
      draft.marketTabApps = insertAtIndex(draft.marketTabApps, clonedApp, insertIdx);
      state.isDirty = true;
    },

    removeApp: (state, action: PayloadAction<{ tabId: string; appId: string }>) => {
      const { tabId, appId } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== appId);
      state.isDirty = true;
    },

    renameAppTab: (
      state,
      action: PayloadAction<{ tabId: string; appId: string; itemId: string; name: string }>
    ) => {
      const { tabId, appId, itemId, name } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const app = draft.marketTabApps.find(a => a.id === appId);
      if (!app) return;

      const tab = app.tabs.find(t => t.id === itemId);
      if (tab) {
        tab.name = name;
        state.isDirty = true;
      }
    },

    updateAppTab: (
      state,
      action: PayloadAction<{ tabId: string; appId: string; itemId: string; tab: WidgetV2 }>
    ) => {
      const { tabId, appId, itemId, tab } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const app = draft.marketTabApps.find(a => a.id === appId);
      if (!app) return;

      const index = app.tabs.findIndex(t => t.id === itemId);
      if (index !== -1) {
        app.tabs[index] = cloneTab(tab);
        state.isDirty = true;
      }
    },

    reorderApps: (
      state,
      action: PayloadAction<{ tabId: string; fromIndex: number; toIndex: number }>
    ) => {
      const { tabId, fromIndex, toIndex } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      draft.marketTabApps = reorderArray(draft.marketTabApps, fromIndex, toIndex);
      state.isDirty = true;
    },

    // ========================================================================
    // App Tab Operations (tabs within app)
    // ========================================================================

    addTabToApp: (
      state,
      action: PayloadAction<{
        tabId: string;
        appId: string;
        tab: WidgetV2;
        insertAtIndex?: number;
      }>
    ) => {
      const { tabId, appId, tab, insertAtIndex: insertIdx } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const app = draft.marketTabApps.find(a => a.id === appId);
      if (!app) return;

      const clonedTab = cloneTab(tab);
      app.tabs = insertAtIndex(app.tabs, clonedTab, insertIdx);
      state.isDirty = true;
    },

    removeTabFromApp: (
      state,
      action: PayloadAction<{ tabId: string; appId: string; itemId: string }>
    ) => {
      const { tabId, appId, itemId } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const app = draft.marketTabApps.find(a => a.id === appId);
      if (!app) return;

      app.tabs = app.tabs.filter(t => t.id !== itemId);

      if (app.tabs.length === 0) {
        draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== appId);
      }

      state.isDirty = true;
    },

    moveTabBetweenApps: (
      state,
      action: PayloadAction<{
        tabId: string;
        itemId: string;
        fromAppId: string;
        toAppId: string;
        insertAtIndex?: number;
      }>
    ) => {
      const { tabId, itemId, fromAppId, toAppId, insertAtIndex: insertIdx } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      if (fromAppId === toAppId) return;

      const sourceApp = draft.marketTabApps.find(a => a.id === fromAppId);
      const targetApp = draft.marketTabApps.find(a => a.id === toAppId);
      if (!sourceApp || !targetApp) return;

      const tabToMove = sourceApp.tabs.find(t => t.id === itemId);
      if (!tabToMove) return;

      const clonedTab = cloneTab(tabToMove);

      sourceApp.tabs = sourceApp.tabs.filter(t => t.id !== itemId);
      targetApp.tabs = insertAtIndex(targetApp.tabs, clonedTab, insertIdx);

      if (sourceApp.tabs.length === 0) {
        draft.marketTabApps = draft.marketTabApps.filter(a => a.id !== fromAppId);
      }

      state.isDirty = true;
    },

    reorderTabsInsideApp: (
      state,
      action: PayloadAction<{
        tabId: string;
        appId: string;
        fromIndex: number;
        toIndex: number;
      }>
    ) => {
      const { tabId, appId, fromIndex, toIndex } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const app = draft.marketTabApps.find(a => a.id === appId);
      if (!app) return;

      app.tabs = reorderArray(app.tabs, fromIndex, toIndex);
      state.isDirty = true;
    },

    // ========================================================================
    // Tile Operations
    // ========================================================================

    addTile: (
      state,
      action: PayloadAction<{
        tabId: string;
        tile: TileBigNumber;
        insertAtIndex?: number;
      }>
    ) => {
      const { tabId, tile, insertAtIndex: insertIdx } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const clonedTile = cloneTile(tile);
      draft.tiles = insertAtIndex(draft.tiles, clonedTile, insertIdx);
      state.isDirty = true;
    },

    removeTile: (state, action: PayloadAction<{ tabId: string; tileId: string }>) => {
      const { tabId, tileId } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      draft.tiles = draft.tiles.filter(t => t.id !== tileId);
      state.isDirty = true;
    },

    updateTile: (
      state,
      action: PayloadAction<{ tabId: string; tileId: string; tile: TileBigNumber }>
    ) => {
      const { tabId, tileId, tile } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      const index = draft.tiles.findIndex(t => t.id === tileId);
      if (index !== -1) {
        draft.tiles[index] = cloneTile(tile);
        state.isDirty = true;
      }
    },

    reorderTiles: (
      state,
      action: PayloadAction<{ tabId: string; fromIndex: number; toIndex: number }>
    ) => {
      const { tabId, fromIndex, toIndex } = action.payload;
      const draft = state.tabDrafts[tabId];
      if (!draft) return;

      draft.tiles = reorderArray(draft.tiles, fromIndex, toIndex);
      state.isDirty = true;
    },

    // ========================================================================
    // Cross-Dashboard Operations
    // ========================================================================

    createDashboardDraft: (state, action: PayloadAction<{ name: string }>) => {
      const { name } = action.payload;
      if (!state.otherDashboardDrafts[name]) {
        state.otherDashboardDrafts[name] = { draftTabs: [], tabDrafts: {} };
      }
    },

    /**
     * Initialize another dashboard's draft from server data.
     * This preserves all existing tabs when moving items to that dashboard.
     */
    initializeOtherDashboardDraft: (
      state,
      action: PayloadAction<{ dashboardName: string; serverData: DashboardTab }>
    ) => {
      const { dashboardName, serverData } = action.payload;

      // Skip if it's the current dashboard (already initialized)
      if (isCurrentDashboard(state, dashboardName)) {
        return;
      }

      // Skip if draft already exists (preserve any modifications)
      if (state.otherDashboardDrafts[dashboardName]) {
        return;
      }

      // Convert server data to draft structure
      const { draftTabs, tabDrafts } = mapServerDataToDraft(serverData, dashboardName);
      state.otherDashboardDrafts[dashboardName] = { draftTabs, tabDrafts };
    },

    moveAppToOtherDashboard: (
      state,
      action: PayloadAction<{
        targetDashboardName: string;
        targetTabId: string;
        app: MarketAppWithTabs;
      }>
    ) => {
      const { targetDashboardName, targetTabId, app } = action.payload;

      // Handle move within the current dashboard
      if (isCurrentDashboard(state, targetDashboardName)) {
        const targetDraft = state.tabDrafts[targetTabId];
        if (targetDraft) {
          // Ensure unique ID and clone
          const appWithUniqueId = ensureUniqueAppId(app, targetDraft.marketTabApps);
          targetDraft.marketTabApps.push(cloneApp(appWithUniqueId));
          state.isDirty = true;
          markDashboardDirty(state, state.dashboardName);
        }
        return;
      }

      // Ensure dashboard and tab drafts exist
      const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
      const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);

      // Ensure unique ID and add app to end
      const appWithUniqueId = ensureUniqueAppId(app, tabDraft.marketTabApps);
      tabDraft.marketTabApps.push(cloneApp(appWithUniqueId));
      markDashboardDirty(state, targetDashboardName);
    },

    moveTileToOtherDashboard: (
      state,
      action: PayloadAction<{
        targetDashboardName: string;
        targetTabId: string;
        tile: TileBigNumber;
      }>
    ) => {
      const { targetDashboardName, targetTabId, tile } = action.payload;

      // Handle move within the current dashboard
      if (isCurrentDashboard(state, targetDashboardName)) {
        const targetDraft = state.tabDrafts[targetTabId];
        if (targetDraft) {
          targetDraft.tiles.push(cloneTile(tile));
          state.isDirty = true;
          markDashboardDirty(state, state.dashboardName);
        }
        return;
      }

      // Ensure dashboard and tab drafts exist
      const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
      const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);

      // Add tile to end
      tabDraft.tiles.push(cloneTile(tile));
      markDashboardDirty(state, targetDashboardName);
    },

    moveAppTabToOtherDashboard: (
      state,
      action: PayloadAction<{
        targetDashboardName: string;
        targetTabId: string;
        tab: WidgetV2;
      }>
    ) => {
      const { targetDashboardName, targetTabId, tab } = action.payload;

      const newAppId = crypto.randomUUID();
      const newApp = {
        id: newAppId,
        tabs: [cloneTab(tab)]
      };

      // Handle move within the current dashboard
      if (isCurrentDashboard(state, targetDashboardName)) {
        const targetDraft = state.tabDrafts[targetTabId];
        if (targetDraft) {
          targetDraft.marketTabApps.push(newApp);
          state.isDirty = true;
          markDashboardDirty(state, state.dashboardName);
        }
        return;
      }

      // Ensure dashboard and tab drafts exist
      const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);
      const tabDraft = ensureTabDraft(dashboardDraft, targetTabId);

      // Add new app with this tab at the end
      tabDraft.marketTabApps.push(newApp);
      markDashboardDirty(state, targetDashboardName);
    },

    moveTabToOtherDashboard: (
      state,
      action: PayloadAction<{
        targetDashboardName: string;
        tabId: string;
        tabName: string;
      }>
    ) => {
      const { targetDashboardName, tabId, tabName } = action.payload;

      const sourceTab = state.draftTabs.find(t => t.id === tabId);
      const sourceContent = state.tabDrafts[tabId];

      if (sourceTab && sourceContent) {
        // Handle move within the current dashboard (no-op)
        if (isCurrentDashboard(state, targetDashboardName)) {
          return;
        }

        // Ensure dashboard draft exists
        const dashboardDraft = ensureDashboardDraft(state, targetDashboardName);

        // Add tab to target dashboard
        dashboardDraft.draftTabs.push({ id: tabId, name: tabName, sourceName: null });
        dashboardDraft.tabDrafts[tabId] = {
          marketTabApps: sourceContent.marketTabApps.map(a => cloneApp(a)),
          tiles: sourceContent.tiles.map(t => cloneTile(t))
        };

        markDashboardDirty(state, targetDashboardName);
      }
    },

    ensureTabExistsInOtherDashboard: (
      state,
      action: PayloadAction<{ dashboardName: string; tabId: string; tabName: string }>
    ) => {
      const { dashboardName, tabId, tabName } = action.payload;

      // Handle current dashboard
      if (isCurrentDashboard(state, dashboardName)) {
        if (!state.tabDrafts[tabId]) {
          // Generate unique name if there's a conflict
          const uniqueName = generateUniqueTabName(state.draftTabs, tabName);
          state.draftTabs.push({ id: tabId, name: uniqueName, sourceName: null });
          state.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
          state.isDirty = true;
          markDashboardDirty(state, state.dashboardName);
        }
        return;
      }

      // Ensure dashboard draft exists
      const dashboardDraft = ensureDashboardDraft(state, dashboardName);

      // Create tab if it doesn't exist
      if (!dashboardDraft.tabDrafts[tabId]) {
        // Generate unique name if there's a conflict
        const uniqueName = generateUniqueTabName(dashboardDraft.draftTabs, tabName);
        dashboardDraft.draftTabs.push({ id: tabId, name: uniqueName, sourceName: null });
        dashboardDraft.tabDrafts[tabId] = { marketTabApps: [], tiles: [] };
      }
    },

    // ========================================================================
    // Batch Update for Current Tab (for DnD operations)
    // ========================================================================

    updateCurrentTabApps: (state, action: PayloadAction<{ apps: MarketAppWithTabs[] }>) => {
      const draft = getCurrentTabDraft(state);
      if (!draft) return;

      draft.marketTabApps = action.payload.apps.map(app => cloneApp(app));
      state.isDirty = true;
    },

    updateCurrentTabTiles: (state, action: PayloadAction<{ tiles: TileBigNumber[] }>) => {
      const draft = getCurrentTabDraft(state);
      if (!draft) return;

      draft.tiles = action.payload.tiles.map(tile => cloneTile(tile));
      state.isDirty = true;
    },

    // ========================================================================
    // UI State
    // ========================================================================

    setInsertPosition: (
      state,
      action: PayloadAction<{ index: number | null; afterId: string | null } | null>
    ) => {
      state.insertPosition = action.payload;
    }
  }
});

export const {
  initializeDraft,
  resetDraft,
  resetToEmpty,
  markDirty,
  renameDashboard,
  setCurrentTab,
  addTab,
  renameTab,
  deleteTab,
  reorderTabs,
  addApp,
  removeApp,
  moveAppTabToOtherDashboard,
  renameAppTab,
  updateAppTab,
  reorderApps,
  addTabToApp,
  removeTabFromApp,
  moveTabBetweenApps,
  reorderTabsInsideApp,
  addTile,
  removeTile,
  updateTile,
  reorderTiles,
  updateCurrentTabApps,
  updateCurrentTabTiles,
  setInsertPosition,
  setAllDashboardsData,
  createDashboardDraft,
  initializeOtherDashboardDraft,
  moveAppToOtherDashboard,
  moveTileToOtherDashboard,
  moveTabToOtherDashboard,
  ensureTabExistsInOtherDashboard
} = dashboardEditSlice.actions;

export default dashboardEditSlice.reducer;
